import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { Btn } from '../components/Input';
import { IconCheckCircle } from '../components/AuthIcons';
import { IconEye, IconEyeOff } from '../components/Icons';

export default function ResetPassword({ token, onSuccess }) {
  const { t } = useTranslation();
  const [haslo, setHaslo]         = useState('');
  const [haslo2, setHaslo2]       = useState('');
  const [showHaslo, setShowHaslo] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (haslo.length < 6) { setError(t('auth.passwordTooShort')); return; }
    if (haslo !== haslo2) { setError(t('auth.passwordMismatch')); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, haslo);
      setDone(true);
      // Wyczyść token z URL
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(onSuccess, 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 800, color: 'var(--acc2)', letterSpacing: '-0.02em', marginBottom: '6px' }}>
            ScanPilots
          </h1>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', fontWeight: 500 }}>
            {t('auth.tagline')}
          </p>
        </div>

        <div className="ds-card" style={{ padding: '28px 24px' }}>
          {done ? (
            /* ── Sukces ── */
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}><IconCheckCircle size={56} /></div>
              <h2 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ok2)', marginBottom: '10px' }}>
                {t('auth.resetSuccess')}
              </h2>
              <p style={{ fontSize: '12px', color: 'var(--ink3)', lineHeight: '1.6' }}>
                {t('auth.resetSuccessDesc')}
              </p>
            </div>
          ) : (
            /* ── Formularz ── */
            <>
              <h2 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '6px' }}>
                {t('auth.setNewPassword')}
              </h2>
              <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '20px', lineHeight: '1.5' }}>
                {t('auth.setNewPasswordDesc')}
              </p>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Nowe hasło */}
                <div>
                  <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', display: 'block', marginBottom: '5px' }}>
                    {t('auth.newPassword')}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showHaslo ? 'text' : 'password'}
                      value={haslo}
                      onChange={e => setHaslo(e.target.value)}
                      placeholder={t('auth.passwordMin')}
                      required
                      autoFocus
                      className="ds-input"
                      style={{ width: '100%', paddingRight: '40px' }}
                    />
                    <button type="button" onClick={() => setShowHaslo(v => !v)}
                      aria-label={showHaslo ? 'Ukryj hasło' : 'Pokaż hasło'}
                      style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink3)', display: 'flex', alignItems: 'center', padding: '2px' }}>
                      {showHaslo ? <IconEyeOff className="w-4 h-4" /> : <IconEye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Powtórz hasło */}
                <div>
                  <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', display: 'block', marginBottom: '5px' }}>
                    {t('auth.confirmPassword')}
                  </label>
                  <input
                    type={showHaslo ? 'text' : 'password'}
                    value={haslo2}
                    onChange={e => setHaslo2(e.target.value)}
                    placeholder={t('auth.repeatPassword')}
                    required
                    className="ds-input"
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Pasek siły hasła */}
                {haslo.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', gap: '3px', marginBottom: '4px' }}>
                      {[1, 2, 3, 4].map(i => {
                        const strength = haslo.length >= 12 ? 4 : haslo.length >= 8 ? 3 : haslo.length >= 6 ? 2 : 1;
                        const colors = ['var(--err2)', 'var(--acc)', '#e8b84b', 'var(--ok2)'];
                        return (
                          <div key={i} style={{
                            flex: 1, height: '3px', borderRadius: '2px',
                            background: i <= strength ? colors[strength - 1] : 'var(--bg4)',
                            transition: 'background 0.2s',
                          }} />
                        );
                      })}
                    </div>
                    <p style={{ fontSize: '9px', color: 'var(--ink3)' }}>
                      {haslo.length < 6 ? t('auth.passwordTooShort') :
                       haslo.length < 8 ? t('auth.passwordWeak') :
                       haslo.length < 12 ? t('auth.passwordOk') :
                       t('auth.passwordStrong')}
                    </p>
                  </div>
                )}

                {error && (
                  <div style={{
                    padding: '10px 12px',
                    background: 'rgba(176,88,72,0.12)',
                    border: '1px solid rgba(176,88,72,0.3)',
                    borderRadius: '8px',
                    fontSize: '11px',
                    color: 'var(--err2)',
                  }}>
                    {error}
                  </div>
                )}

                <Btn type="submit" variant="primary" disabled={loading}
                  style={{ width: '100%', padding: '10px 14px', fontSize: '11px', marginTop: '4px' }}>
                  {loading ? t('auth.saving') : t('auth.saveNewPassword')}
                </Btn>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
